import axios from 'axios'
import * as React from 'react'
import Select from 'react-select'
import { connect } from 'react-redux'
import classNames from 'classnames'
import { Button, Dialog, Icon, TextField } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import store from 'src/store'
import { mainApi } from 'src/config'
import { Translate } from 'src/translation/'
import { getInitials, checkIfDevelopmentStage } from 'src/utils/common'
import { ReduxState } from 'src/store/reducers'
import { UserProfile } from 'src/interfaces/User'
import { countries } from 'src/constants/countries'
import Spinner from 'src/components/reusable/Spinner'
import { setUserProfile } from 'src/auth/store/actions/user.actions'
import { getUserPermissions } from 'src/auth/cognito/organizationApi'
import { addNotification } from '../../../NotificationWrapper'
import styles from './UserProfileDialog.style'

type Props = {
  classes?: {
    error: string
    wrapper: string
    saveBtn: string
    clearImg: string
    userName: string
    inputField: string
    crossButton: string
    innerWrapper: string
    profilePicText: string
    nonEditableText: string
    userDetailTitle: string
    userDetailsWrapper: string
    uploadPictureButton: string
    welcomeMessageTitle: string
    profilePictureDefault: string
    inputRow: string
    inputRowSingle: string
  }
  profile?: UserProfile
  open: boolean
  tubeColor: string
  onClose: () => void
  translate: Translate
  saveUserProfile: (data: any) => Promise<any>
}

interface FormData {
  firstName: string
  lastName: string
  jobTitle: string
  phoneNumber: string
  companyName: string
  manager: string
  country: string
}

const SELECT_STYLES = {
  control: (provided) => ({
    ...provided,
    border: '1px solid #d8dbe0',
    borderRadius: 40,
    padding: '1px 8px',
    boxShadow: 'none',
    minHeight: '36px',
    background: 'white',
    fontSize: 14,
    color: '#323335',
    '&:hover': {
      borderColor: '#d8dbe0'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 6px'
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    fontSize: 14,
    color: '#323335'
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.4)'
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: 14,
    color: '#323335',
    margin: 0,
    padding: 0
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '4px',
    color: '#94969a'
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '4px',
    color: '#94969a'
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    fontSize: 14
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
}

const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'])

const UserProfileDialog: React.FC<Props> = ({
  open,
  tubeColor,
  onClose,
  classes,
  profile,
  translate,
  saveUserProfile
}) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [profilePicPreview, setProfilePicPreview] = React.useState<string>('')
  const [isSaving, setIsSaving] = React.useState(false)
  const isMountedRef = React.useRef(true)

  const validationSchema = React.useMemo(
    () =>
      yup.object().shape({
        firstName: yup.string().required(String(translate('required'))),
        lastName: yup.string().required(String(translate('required'))),
        jobTitle: yup.string(),
        phoneNumber: yup.string().matches(/^[\d\s\-+()]*$/, String(translate('invalid-phone-format'))),
        companyName: yup.string(),
        manager: yup.string(),
        country: yup.string()
      }),
    [translate]
  )

  const defaultValues = React.useMemo(
    () => ({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      jobTitle: profile?.jobTitle || '',
      phoneNumber: profile?.phoneNumber || '',
      companyName: profile?.companyName || '',
      manager: profile?.manager || '',
      country: profile?.country || ''
    }),
    [
      profile?.firstName,
      profile?.lastName,
      profile?.jobTitle,
      profile?.phoneNumber,
      profile?.companyName,
      profile?.manager,
      profile?.country
    ]
  )

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues
  })

  const countryOptions = React.useMemo(() => countries.map((country) => ({ label: country, value: country })), [])

  const userName = React.useMemo(
    () => `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim(),
    [profile?.firstName, profile?.lastName]
  )

  const displayName = userName || profile?.email || ''
  const avatarInitials = userName ? getInitials(userName) : (profile?.email || '').slice(0, 2).toUpperCase()

  React.useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  React.useEffect(() => {
    return () => {
      if (profilePicPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(profilePicPreview)
      }
    }
  }, [profilePicPreview])

  React.useEffect(() => {
    if (open && profile) {
      reset(defaultValues)
      setProfilePicPreview(profile.profilePic || '')
      setSelectedFile(null)
    }
  }, [open, profile, reset, defaultValues])

  const handleFileChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0]
      if (!file) return

      if (!ALLOWED_IMAGE_TYPES.has(file.type.toLowerCase())) {
        addNotification({
          children: (
            <div className="custom-notification">
              <div className={classNames(classes.error, 'custom-notification__icon')}>
                <Icon>warning</Icon>
              </div>
              <div className="custom-notification__text">
                <p className="word-break">
                  {translate('invalid-file-type') || 'Invalid file type. Please upload JPEG, PNG, GIF, or WebP.'}
                </p>
              </div>
            </div>
          ),
          position: 'tr',
          autoDismiss: 5,
          level: 'error'
        })

        e.target.value = ''
        return
      }

      if (profilePicPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(profilePicPreview)
      }

      const url = URL.createObjectURL(file)
      setProfilePicPreview(url)
      setSelectedFile(file)
    },
    [profilePicPreview, classes.error, translate]
  )

  const handleClearImage = React.useCallback(() => {
    if (profilePicPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(profilePicPreview)
    }
    setProfilePicPreview('')
    setSelectedFile(null)
  }, [profilePicPreview])

  const showErrorNotification = React.useCallback(() => {
    addNotification({
      children: (
        <div className="custom-notification">
          <div className={classNames(classes.error, 'custom-notification__icon')}>
            <Icon>warning</Icon>
          </div>
          <div className="custom-notification__text">
            <p className="word-break">{translate('error-saving')}</p>
          </div>
        </div>
      ),
      position: 'tr',
      autoDismiss: 15,
      level: 'error'
    })
  }, [classes.error, translate])

  const onSubmit = async (data: FormData) => {
    if (!isMountedRef.current) {
      return
    }

    setIsSaving(true)

    try {
      let pathUrl = null

      if (selectedFile) {
        if (!isMountedRef.current) {
          return
        }

        const isDevStage = checkIfDevelopmentStage()
        const mainApiUrl = mainApi(isDevStage)

        const apiRes = await axios.post(
          `${mainApiUrl.command}/getGenericS3UploadSignedUrl`,
          { fileExt: selectedFile.name.split('.').pop() },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        )

        if (!isMountedRef.current) {
          return
        }

        pathUrl = apiRes.data

        await axios.put(pathUrl.signedUrl, selectedFile, {
          headers: { 'content-type': selectedFile.type }
        })

        if (!isMountedRef.current) return
      }

      await saveUserProfile({
        ...data,
        ...(!profilePicPreview && { profilePic: '' }),
        ...(pathUrl && { profilePic: pathUrl.s3Path })
      })

      if (!isMountedRef.current) {
        return
      }

      const userData = await getUserPermissions()

      if (!isMountedRef.current) {
        return
      }
      setUserProfile(userData.userData)(store.dispatch)

      if (!isMountedRef.current) {
        return
      }

      onClose()
    } catch (error) {
      if (!isMountedRef.current) {
        return
      }

      console.error('Error saving user profile:', error)
      showErrorNotification()
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false)
      }
    }
  }

  const renderTextField = (name: keyof FormData, labelKey: string, type?: string) => (
    <div>
      <div className={classes.userDetailTitle}>{translate(labelKey)}</div>
      <Controller
        name={name}
        control={control}
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            name={name}
            type={type}
            placeholder={String(translate(`${labelKey}-placeholder`))}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            fullWidth
            InputProps={{
              disableUnderline: true,
              className: classes.inputField
            }}
            FormHelperTextProps={{ style: { marginLeft: 0, marginTop: 4 } }}
          />
        )}
      />
    </div>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" PaperProps={{ style: { borderRadius: 15 } }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.wrapper}>
          <div className={classes.innerWrapper}>
            <Button type="button" className={classes.crossButton} onClick={onClose} aria-label="Close dialog">
              <Icon>close</Icon>
            </Button>

            <div className={classes.welcomeMessageTitle}>{translate('welcome-msg')}</div>
            <div className={classes.userName}>{displayName}</div>
          </div>

          <div className={classes.userDetailsWrapper}>
            {profilePicPreview ? (
              <div className={classes.profilePictureDefault}>
                <img src={profilePicPreview} alt="pic" className="w-100" />
                <Button
                  type="button"
                  className={classes.clearImg}
                  onClick={handleClearImage}
                  aria-label="Remove profile picture"
                >
                  <Icon style={{ color: tubeColor }}>close</Icon>
                  <span>{translate('remove')}</span>
                </Button>
              </div>
            ) : (
              <div className={classes.profilePictureDefault}>
                <span className={classes.profilePicText}>{avatarInitials}</span>
              </div>
            )}

            <div className={classes.uploadPictureButton}>
              <input
                accept="image/*"
                id="icon-button-file"
                onChange={handleFileChange}
                type="file"
                style={{ display: 'none' }}
              />
              <label htmlFor="icon-button-file">
                <div className="cursor-pointer">{translate('upload-picture')}</div>
              </label>
            </div>

            <div className={classes.userDetailTitle} style={{ textAlign: 'center' }}>
              {translate('your-email')}
            </div>
            <div className={classes.nonEditableText}>{profile?.email}</div>

            <div className={classes.inputRow}>
              {renderTextField('firstName', 'first-name')}
              {renderTextField('lastName', 'last-name')}
            </div>

            <div className={classes.inputRow}>
              {renderTextField('companyName', 'company-name')}
              {renderTextField('phoneNumber', 'company-phone', 'tel')}
            </div>

            <div className={classes.inputRow}>
              {renderTextField('jobTitle', 'job-title')}
              {renderTextField('manager', 'manager')}
            </div>

            <div className={classes.inputRowSingle}>
              <div>
                <div className={classes.userDetailTitle}>{translate('location')}</div>
                <Controller
                  name="country"
                  control={control}
                  render={({ onChange, onBlur, value }) => (
                    <Select
                      onBlur={onBlur}
                      value={value ? { label: value, value } : null}
                      onChange={(option: any) => onChange(option?.value || '')}
                      placeholder={String(translate('country-placeholder'))}
                      styles={SELECT_STYLES}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      options={countryOptions}
                      isClearable
                    />
                  )}
                />
              </div>
            </div>

            <Button type="submit" className={classes.saveBtn} disabled={isSaving}>
              {isSaving ? <Spinner /> : translate('save')}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  )
}

const mapStateToProps = (state: ReduxState) => ({
  profile: state.auth.user.profile
})

export default connect(mapStateToProps)(withStyles(styles, { withTheme: true })(UserProfileDialog))
